import { Stack, Typography } from '@mui/material';

import { CloseButton } from '@/components/form/baseInputs/Button/CloseButton';
import { FloatingMenuContainer } from '@/components/layout/FloatingMenuContainer';
import { AnnotationNavigation } from '@/modules/pdf/DocumentSearch/components/AnnotationNavigation';
import { formatReferenceCountMessage } from '@/modules/pdf/DocumentSearch/DocumentSearchBar.utils';
import { useAnnotatedPDFViewerContext } from '@/modules/pdf/PDFViewer/AnnotatedPDFViewer/context/AnnotatedPDFViewer.context';
import { DocumentFooterProps } from '@/modules/pdf/PDFViewer/DocumentFooter';
import { THEME_SHADOWS } from '@/styles/themes/common';
import { COLORS } from '@/styles/tokens/colors';

const MAX_BAR_WIDTH_PX = 640;

/**
 * The slimmed-down references bar for the chat document viewer — the
 * DocumentSearchBar variant without a search input or summarized answer: just
 * which cited passages are highlighted and controls to step between them.
 * Renders only while highlights are on the document (citation side content
 * opens with them seeded); the close button clears them, hiding the bar until
 * another citation is clicked. Anchored near the bottom of the viewer's
 * scroll container.
 */
export function ChatDocumentReferencesBar({
  isDocumentLoading,
}: DocumentFooterProps) {
  const {
    annotations,
    activeAnnotationIndex,
    moveActiveAnnotation,
    setActiveAnnotationIndex,
    setAnnotations,
    searchQuery,
  } = useAnnotatedPDFViewerContext();

  if (isDocumentLoading || !annotations?.length) {
    return null;
  }

  const handleDismiss = () => {
    setAnnotations(undefined);
    setActiveAnnotationIndex(0);
  };

  return (
    <FloatingMenuContainer
      offset={{ bottom: 0 }}
      data-testid="chat-document-references-bar"
    >
      {/* Centered with a capped width so the bar doesn't keep growing with
          the panel in wide layouts. */}
      <Stack
        sx={{
          borderRadius: 1,
          boxShadow: THEME_SHADOWS.md,
          maxWidth: MAX_BAR_WIDTH_PX,
          mx: 'auto',
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={1}
          sx={{
            backgroundColor: COLORS.PRIMITIVES.WHITE,
            border: `solid 1px ${COLORS.GRAY[200]}`,
            borderBottom: 'none',
            borderRadius: 1,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            px: 2,
            py: 1.5,
          }}
        >
          <Stack gap={0.5}>
            <Typography variant="title-s" color={COLORS.NAVY[800]}>
              References
            </Typography>
            <Typography variant="body-s">
              {/* The chat panel seeds highlights from a clicked citation, not
                  a typed search, so the query (and its "for …" suffix) is
                  usually empty. */}
              {formatReferenceCountMessage(annotations, searchQuery)}
            </Typography>
          </Stack>
          <CloseButton
            size="xs"
            variant="transparent"
            onClick={handleDismiss}
          />
        </Stack>
        <AnnotationNavigation
          variant="flat-top"
          displayIndex={activeAnnotationIndex + 1}
          totalCount={annotations.length}
          onPrevious={() => moveActiveAnnotation(-1)}
          onNext={() => moveActiveAnnotation(1)}
        />
      </Stack>
    </FloatingMenuContainer>
  );
}
